'use client';

import { useIsMobile } from "@/hooks/use-is-mobile";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "../ui/drawer";
import { useTranslations } from "next-intl";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import Image from "next/image";
import { DialogClose } from "@radix-ui/react-dialog";
import { Button } from "../ui/button";
import { useEffect, useState } from "react";
import { appConfig } from "@/app-config";

type Props = {
  delayMs?: number;
  cooldownDays?: number;
  onOpenChangeExternal?: (open: boolean) => void;
};

const STORAGE_KEY = 'tibetmed_certificates_modal_next_show_at';

function nowMs() {
  return Date.now();
}

function daysToMs(days: number) {
  return days * 24 * 60 * 60 * 1000;
}

function getNextShowAt(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function setNextShowAt(ts: number) {
  try {
    localStorage.setItem(STORAGE_KEY, String(ts));
  } catch {
    // ignore (private mode / denied)
  }
}

export default function CertificatesModal({ delayMs = 600, cooldownDays = 1, onOpenChangeExternal }: Props) {
  const isMobile = useIsMobile();
  const t = useTranslations('certificates_modal');
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    const nextShowAt = getNextShowAt();
    const allowed = !nextShowAt || nowMs() >= nextShowAt;
    if (!allowed) return;

    const id = window.setTimeout(() => {
      setOpen(true);
      onOpenChangeExternal?.(true);
    }, delayMs);

    return () => window.clearTimeout(id);
  }, [delayMs, onOpenChangeExternal]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChangeExternal?.(next);

    if (!next) {
      setNextShowAt(nowMs() + daysToMs(cooldownDays));
    }
  };

  const certificateImage = (
    <Image
      src="/images/certificates/2.png"
      alt={t('certificate')}
      width={526}
      height={340}
      priority={false}
    />
  );

  return (
    isMobile ? (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{t('title')}</DrawerTitle>
            <DrawerDescription>{t('description')}</DrawerDescription>
          </DrawerHeader>
          <div className="flex justify-center">
            {open ? certificateImage : null}
          </div>
          <DrawerFooter>
            <Button asChild className="bg-brand text-white hover:bg-accent-blue">
              <a href={`tel:+37368408484`} aria-label={`${t('call')} ${appConfig.contacts.phone_mob}`}>
                {t('call')}
              </a>
            </Button>
            <DrawerClose asChild>
              <Button variant="outline">{t('close')}</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    ) : (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogDescription>{t('description')}</DialogDescription>
          </DialogHeader>
          {open ? certificateImage : null}
          <DialogFooter>
            <Button asChild className="bg-brand text-white hover:bg-accent-blue">
              <a href={`tel:+37368408484`} aria-label={`${t('call')} ${appConfig.contacts.phone_mob}`}>
                {t('call')}
              </a>
            </Button>
            <DialogClose asChild>
              <Button variant="outline">{t('close')}</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  )
}
